// frontend/src/components/community/QuickViewModal.js
import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, Package, TrendingUp, Star, ShoppingBag, MessageSquare, Heart, Clock, CheckCircle } from 'lucide-react';
import { toast } from 'react-hot-toast';

const QuickViewModal = ({ item, type, business, onClose, onOrder, onInquire, onToggleFavorite, isFavorited }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(isFavorited || false);
  const [isAnimating, setIsAnimating] = useState(false);

  const isProduct = type === 'product';
  const itemName = isProduct ? item.name : item.title;
  const itemPrice = isProduct ? item.price : item.basePrice;
  const images = item.images || [];
  const itemRating = item.rating || 0;
  const reviewCount = item.reviewCount || item._count?.reviews || 0;
  const isLowStock = isProduct && item.quantity <= 5 && item.quantity > 0;
  const isOutOfStock = isProduct && item.quantity === 0;

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 sm:h-5 sm:w-5 ${
              star <= Math.floor(rating)
                ? 'text-yellow-400 fill-current'
                : star <= rating
                ? 'text-yellow-400 fill-current opacity-50'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const handleFavoriteClick = async () => {
    setIsFavorite(!isFavorite);
    if (onToggleFavorite) {
      await onToggleFavorite();
    }
    toast.success(isFavorite ? 'Removed from favorites' : 'Added to favorites');
  };

  const nextImage = () => {
    if (images.length > 0) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentImageIndex((prev) => (prev + 1) % images.length);
        setIsAnimating(false);
      }, 200);
    }
  };

  const prevImage = () => {
    if (images.length > 0) {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
        setIsAnimating(false);
      }, 200);
    }
  };

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={onClose}>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity" />

      {/* Modal */}
      <div className="flex items-center justify-center min-h-screen p-3 sm:p-4">
        <div 
          className="relative bg-[#E8ECF5] rounded-2xl shadow-neomorph max-w-4xl w-full mx-auto overflow-hidden animate-slide-up"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-3 right-3 z-10 p-1.5 sm:p-2 bg-white rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-gray-500"
          >
            <X className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>

          <div className="flex flex-col md:flex-row">
            {/* Image Gallery Section */}
            <div className="md:w-1/2 bg-gradient-to-br from-gray-100 to-gray-200 p-3 sm:p-4">
              <div className="relative">
                {/* Main Image */}
                <div className="aspect-square rounded-xl overflow-hidden bg-white shadow-neomorph-inset">
                  {images.length > 0 ? (
                    <img
                      src={images[currentImageIndex]}
                      alt={itemName}
                      className={`w-full h-full object-cover transition-all duration-300 ${isAnimating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      {isProduct ? (
                        <Package className="h-16 w-16 sm:h-20 sm:w-20 text-gray-400" />
                      ) : (
                        <TrendingUp className="h-16 w-16 sm:h-20 sm:w-20 text-gray-400" />
                      )}
                    </div>
                  )}
                </div>

                {/* Image Navigation */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-2 top-1/2 transform -translate-y-1/2 p-1.5 sm:p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-neomorph hover:shadow-neomorph-hover transition-all"
                    >
                      <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1.5 sm:p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-neomorph hover:shadow-neomorph-hover transition-all"
                    >
                      <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
                    </button>
                  </>
                )}

                {/* Image Dots */}
                {images.length > 1 && (
                  <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 flex gap-1">
                    {images.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentImageIndex(idx)}
                        className={`h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full transition-all ${
                          idx === currentImageIndex
                            ? 'bg-primary-600 w-3 sm:w-4'
                            : 'bg-gray-400'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Thumbnails */}
              {images.length > 1 && (
                <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                  {images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-lg overflow-hidden border-2 transition-all ${
                        idx === currentImageIndex
                          ? 'border-primary-600 shadow-md'
                          : 'border-transparent opacity-60'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Details Section */}
            <div className="md:w-1/2 p-4 sm:p-6 overflow-y-auto max-h-[60vh] md:max-h-[80vh]">
              {/* Type Badge */}
              <div className="inline-flex items-center px-2 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-lg mb-3">
                {isProduct ? (
                  <>
                    <Package className="h-3 w-3 mr-1" />
                    Product
                  </>
                ) : (
                  <>
                    <TrendingUp className="h-3 w-3 mr-1" />
                    Service
                  </>
                )}
              </div>

              {/* Title */}
              <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">{itemName}</h2>

              {/* Category */}
              <div className="text-xs sm:text-sm text-gray-500 mb-3">
                Category: <span className="font-medium text-gray-700">{item.category}</span>
              </div>

              {/* Rating */}
              {itemRating > 0 && (
                <div className="flex items-center gap-2 mb-3">
                  {renderStars(itemRating)}
                  <span className="text-xs sm:text-sm text-gray-500">({reviewCount} reviews)</span>
                </div>
              )}

              {/* Price */}
              <div className="mb-4">
                <div className="flex items-baseline gap-2">
                  <span className="text-2xl sm:text-3xl font-bold text-primary-600">
                    {formatAmount(itemPrice)}
                  </span>
                  {isProduct && item.compareAtPrice && item.compareAtPrice > itemPrice && (
                    <span className="text-sm text-gray-400 line-through">
                      {formatAmount(item.compareAtPrice)}
                    </span>
                  )}
                </div>
                {isProduct && item.compareAtPrice && item.compareAtPrice > itemPrice && (
                  <p className="text-xs sm:text-sm text-green-600 mt-1">
                    You save {formatAmount(item.compareAtPrice - itemPrice)}
                  </p>
                )}
              </div>

              {/* Stock Status (Products only) */}
              {isProduct && (
                <div className="mb-4">
                  {isOutOfStock ? (
                    <div className="flex items-center gap-2 text-red-600 bg-red-50 px-3 py-2 rounded-lg">
                      <X className="h-4 w-4" />
                      <span className="text-sm font-medium">Out of Stock</span>
                    </div>
                  ) : isLowStock ? (
                    <div className="flex items-center gap-2 text-orange-600 bg-orange-50 px-3 py-2 rounded-lg animate-pulse">
                      <Clock className="h-4 w-4" />
                      <span className="text-sm font-medium">Only {item.quantity} left in stock</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-green-600 bg-green-50 px-3 py-2 rounded-lg">
                      <CheckCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">In Stock ({item.quantity} available)</span>
                    </div>
                  )}
                </div>
              )}

              {/* Description */}
              {item.description && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">Description</h4>
                  <p className="text-xs sm:text-sm text-gray-600 whitespace-pre-line">
                    {item.description}
                  </p>
                </div>
              )}

              {/* Service-specific details */}
              {!isProduct && item.deliveryTime && (
                <div className="mb-4 bg-gray-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock className="h-4 w-4" />
                    <span>Delivery: <span className="font-medium text-gray-800">{item.deliveryTime}</span></span>
                  </div>
                </div>
              )}

              {/* Tags */}
              {item.tags && item.tags.length > 0 && (
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-800 mb-2">Tags</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {item.tags.slice(0, 5).map((tag, idx) => (
                      <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-lg">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-3 border-t border-primary-100/50">
                <button
                  onClick={() => onOrder(item, type)}
                  disabled={isOutOfStock}
                  className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                    isOutOfStock
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-md shadow-primary-500/30 hover:shadow-lg'
                  }`}
                >
                  <ShoppingBag className="h-4 w-4" />
                  {isProduct ? 'Buy Now' : 'Book Now'}
                </button>
                <button
                  onClick={() => onInquire(item, type)}
                  className="flex-1 px-4 py-2.5 bg-white text-gray-700 rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all text-sm font-medium flex items-center justify-center gap-2"
                >
                  <MessageSquare className="h-4 w-4" />
                  Inquire
                </button>
                <button
                  onClick={handleFavoriteClick}
                  className={`px-4 py-2.5 rounded-lg transition-all flex items-center justify-center gap-2 ${
                    isFavorite 
                      ? 'bg-red-500 text-white shadow-md shadow-red-500/30' 
                      : 'bg-white text-gray-700 shadow-neomorph hover:shadow-neomorph-hover'
                  }`}
                >
                  <Heart className={`h-4 w-4 ${isFavorite ? 'fill-current' : ''}`} />
                  <span className="hidden sm:inline">{isFavorite ? 'Saved' : 'Save'}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default QuickViewModal;